"use client";
import {
  CreateWorkplaceRequestParams,
  UpdateWorkplaceRequestParams,
  WorkplaceRequestApprovalResponse,
  WorkplaceRequestBatchUpdateResult,
  WorkplaceRequestStatus,
  WorkplaceRequestWorkflowOverrides
} from "@gettactic/api";
import { getApiErrorMessage } from "@gettactic/helpers/src/errors/getApiErrorMessage";
import { logError } from "@gettactic/helpers/src/errors/logError";
import { useAuthenticated, useI18n } from "@gettactic/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { RequestStatus } from "@/features/settings/components/WorkplaceRequestsPage/workplace-requests-settings/types";
import { useWorkplaceRequestByIdKey } from "@/features/workplace-requests/hooks/useWorkplaceRequestById";
import { getApprovalErrorMessage } from "@/features/workplace-requests/lib/approvalError";
import {
  describeBatchFailures,
  summarizeBatchResults,
  toBatchBodies,
  toFailedBatchResults,
  type WorkplaceRequestUpdateEntry
} from "@/features/workplace-requests/lib/batchUpdate";
import { api } from "@/lib/api/api";

/** How a batch operation reads in its toast: "update"/"updated". */
type BatchOperation = { verb: string; past: string };

const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

type BatchResult<T> = {
  successful: Array<{ requestId: string; result: T }>;
  failed: Array<{ requestId: string; error: unknown }>;
  totalRequested: number;
  totalSuccessful: number;
  totalFailed: number;
};

/**
 * How long to wait before refetching a just-approved request, so its workflow
 * runs have been written. Long enough for the dispatch job in a healthy queue,
 * short enough that the panel doesn't look stuck.
 */
const WORKFLOW_RUN_REFRESH_DELAY_MS = 2500;

export function useWorkplaceRequestMutations() {
  const queryClient = useQueryClient();
  const {
    userContext: { deviceTz, authenticatedUser }
  } = useAuthenticated();
  const tz = authenticatedUser?.user?.time_zone;
  const { templates } = useI18n(authenticatedUser);

  const updateAll = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["workplaceRequests"]
    });
    queryClient.invalidateQueries({
      queryKey: ["workplaceRequestsBySchedule"]
    });
    // Also refresh the open detail view (the view modal reads this query), so
    // in-modal edits reflect without closing and reopening.
    queryClient.invalidateQueries({
      queryKey: [useWorkplaceRequestByIdKey]
    });
    // Keep the sidebar's per-view counts (Unresolved, Needs Triage, ...) in
    // sync — they're a separate query from the request list, so without this
    // they were only refreshing on a hard remount.
    queryClient.invalidateQueries({
      queryKey: ["workplaceRequestStats"]
    });
  }, [queryClient]);

  /**
   * Approving a request enqueues its workflows; core writes a run row only once
   * the dispatch job picks the trigger up, which is after the approval call has
   * already returned. The refetch that follows the response therefore sees no
   * runs at all, and nothing else would fetch the request again — so the
   * automation panel sits blank as if approving had done nothing.
   *
   * One deferred refetch closes that window. It is a courtesy, not a guarantee:
   * a slow queue just means the panel fills in on the next refetch.
   */
  const refreshWorkflowRunsSoon = useCallback(() => {
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: [useWorkplaceRequestByIdKey]
      });
    }, WORKFLOW_RUN_REFRESH_DELAY_MS);
  }, [queryClient]);

  const showBatchToast = useCallback(
    (
      operation: BatchOperation,
      totalSuccessful: number,
      totalFailed: number,
      totalRequested: number,
      failureDetails: string[] = []
    ) => {
      // The reasons follow the summary line, so the user sees which requests
      // failed and why without opening each one.
      const details =
        failureDetails.length > 0 ? `\n${failureDetails.join("\n")}` : "";
      if (totalFailed === 0) {
        if (totalSuccessful === 1) {
          toast.success(`Request ${operation.past} successfully`);
        } else {
          toast.success(
            `${totalSuccessful} requests ${operation.past} successfully`
          );
        }
      } else if (totalSuccessful === 0) {
        toast.error(
          `Failed to ${operation.verb} ${totalFailed} requests${details}`
        );
      } else {
        toast.success(
          `${capitalize(operation.past)} ${totalSuccessful} of ${totalRequested} requests. ${totalFailed} failed.${details}`,
          { duration: 4000 }
        );
      }
    },
    []
  );

  const processBatchResults = useCallback(
    <T>(
      results: PromiseSettledResult<{
        success: boolean;
        requestId: string;
        result?: T;
        error?: unknown;
      }>[]
    ): BatchResult<T> => {
      const successful = results
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<{
            success: true;
            requestId: string;
            result: T;
          }> => result.status === "fulfilled" && result.value.success
        )
        .map((result) => ({
          requestId: result.value.requestId,
          result: result.value.result!
        }));

      const failed = results
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<{
            success: false;
            requestId: string;
            error: unknown;
          }> => result.status === "fulfilled" && !result.value.success
        )
        .map((result) => ({
          requestId: result.value.requestId,
          error: result.value.error
        }));

      return {
        successful,
        failed,
        totalRequested: results.length,
        totalSuccessful: successful.length,
        totalFailed: failed.length
      };
    },
    []
  );

  const updateWorkplaceRequestStatusMutation = useMutation({
    mutationFn: async ({
      requestId,
      status,
      comment
    }: {
      requestId: string;
      status: RequestStatus;
      comment?: string;
    }) => {
      const updates: {
        status: WorkplaceRequestStatus;
        update_comment?: string;
      } = {
        status: status.toLowerCase() as WorkplaceRequestStatus
      };

      if (comment?.trim()) {
        updates.update_comment = comment.trim();
      }

      return await api.client.workplaceRequests.updateWorkplaceRequestStatus(
        requestId,
        status.toLowerCase() as WorkplaceRequestStatus,
        comment,
        tz ?? deviceTz
      );
    },

    onSuccess: () => {
      updateAll();
      toast.success("Request status updated successfully");
    },

    onError: (error) => {
      logError("Error updating workplace request status:", error);
      toast.error(getApiErrorMessage(error, "Failed to update request status"));
    }
  });

  const updateWorkplaceRequestStatus = useCallback(
    (requestId: string, status: RequestStatus, comment?: string) => {
      return updateWorkplaceRequestStatusMutation.mutateAsync({
        requestId,
        status,
        comment
      });
    },
    [updateWorkplaceRequestStatusMutation]
  );

  const updateWorkplaceRequestOwnerMutation = useMutation({
    mutationFn: async ({
      requestId,
      ownerUserId
    }: {
      requestId: string;
      ownerUserId: string | null;
    }) => {
      return await api.client.workplaceRequests.updateWorkplaceRequestOwner(
        requestId,
        ownerUserId,
        tz ?? deviceTz
      );
    },

    onSuccess: () => {
      updateAll();
      toast.success("Request owner updated successfully");
    },

    onError: (error) => {
      logError("Error updating workplace request owner:", error);
      toast.error(getApiErrorMessage(error, "Failed to update request owner"));
    }
  });

  const updateWorkplaceRequestOwner = useCallback(
    (requestId: string, ownerUserId: string | null) => {
      return updateWorkplaceRequestOwnerMutation.mutateAsync({
        requestId,
        ownerUserId
      });
    },
    [updateWorkplaceRequestOwnerMutation]
  );

  const deleteWorkplaceRequestMutation = useMutation({
    mutationFn: async ({ requestId }: { requestId: string }) => {
      return await api.client.workplaceRequests.deleteWorkplaceRequest(
        requestId
      );
    },

    onSuccess: () => {
      updateAll();
      toast.success("Request deleted successfully");
    },

    onError: (error) => {
      logError("Error deleting workplace request:", error);
      toast.error(getApiErrorMessage(error, "Failed to delete request"));
    }
  });

  const deleteWorkplaceRequest = useCallback(
    (requestId: string) => {
      return deleteWorkplaceRequestMutation.mutateAsync({ requestId });
    },
    [deleteWorkplaceRequestMutation]
  );

  const updateWorkplaceRequestMutation = useMutation({
    mutationFn: async ({
      requestId,
      updates
    }: {
      requestId: string;
      updates: UpdateWorkplaceRequestParams;
    }) => {
      return await api.client.workplaceRequests.updateWorkplaceRequest(
        requestId,
        updates,
        tz ?? deviceTz
      );
    },

    onSuccess: () => {
      updateAll();
      toast.success("Request updated successfully");
    },

    onError: (error) => {
      logError("Error updating workplace request:", error);
      toast.error(getApiErrorMessage(error, "Failed to update request"));
    }
  });

  const updateWorkplaceRequest = useCallback(
    (requestId: string, updates: UpdateWorkplaceRequestParams) => {
      return updateWorkplaceRequestMutation.mutateAsync({ requestId, updates });
    },
    [updateWorkplaceRequestMutation]
  );

  const respondToApprovalMutation = useMutation({
    mutationFn: async ({
      requestId,
      status,
      workflowOverrides,
      skipWorkflowIds
    }: {
      requestId: string;
      status: WorkplaceRequestApprovalResponse;
      /** Approver edits to the reservation a workflow would book. */
      workflowOverrides?: WorkplaceRequestWorkflowOverrides;
      /** Workflows the approver chose not to run for this approval. */
      skipWorkflowIds?: string[];
      /**
       * Off when the caller shows the failure itself (the approval modal keeps
       * it inline so the approver can adjust and retry).
       */
      showErrorToast?: boolean;
    }) => {
      return await api.client.workplaceRequests.respondToWorkplaceRequestApproval(
        requestId,
        status,
        tz ?? deviceTz,
        workflowOverrides,
        skipWorkflowIds
      );
    },

    onSuccess: (_result, { status }) => {
      updateAll();
      // Only an approval starts workflows; a rejection has no runs to wait for.
      if (status === "approve") refreshWorkflowRunsSoon();
      toast.success(
        status === "approve" ? "Request approved" : "Request rejected"
      );
    },

    onError: (error, { showErrorToast = true }) => {
      logError("Error responding to workplace request approval:", error);
      if (!showErrorToast) return;
      toast.error(getApprovalErrorMessage(error, templates.displayDate));
    }
  });

  const respondToApproval = useCallback(
    (
      requestId: string,
      status: WorkplaceRequestApprovalResponse,
      workflowOverrides?: WorkplaceRequestWorkflowOverrides,
      options: { showErrorToast?: boolean; skipWorkflowIds?: string[] } = {}
    ) => {
      return respondToApprovalMutation.mutateAsync({
        requestId,
        status,
        workflowOverrides,
        skipWorkflowIds: options.skipWorkflowIds,
        showErrorToast: options.showErrorToast
      });
    },
    [respondToApprovalMutation]
  );

  /**
   * One `PUT /tickets` per batch of up to 100 requests. The endpoint is
   * partial-success, so a request that can't be updated fails on its own
   * entry and the others still go through. A batch whose whole call fails is
   * folded in the same way, so an earlier batch that already committed is
   * still reported and the queue still refreshes.
   */
  const updateMultipleWorkplaceRequestsMutation = useMutation({
    mutationFn: async (requests: WorkplaceRequestUpdateEntry[]) => {
      const results: WorkplaceRequestBatchUpdateResult[] = [];
      for (const body of toBatchBodies(requests)) {
        try {
          const response =
            await api.client.workplaceRequests.updateWorkplaceRequests(
              body,
              tz ?? deviceTz
            );
          // A missing body leaves those requests unanswered, and the summary
          // then counts them as failed rather than silently updated.
          results.push(...(response.result?.results ?? []));
        } catch (error) {
          logError("Batch update of workplace requests failed:", error);
          results.push(
            ...toFailedBatchResults(
              body,
              getApiErrorMessage(error, "Failed to update requests")
            )
          );
        }
      }

      return summarizeBatchResults(requests, results);
    },

    onSuccess: (outcome) => {
      const { totalSuccessful, totalFailed, totalRequested, failed } = outcome;
      updateAll();
      showBatchToast(
        { verb: "update", past: "updated" },
        totalSuccessful,
        totalFailed,
        totalRequested,
        describeBatchFailures(failed)
      );
    },

    onError: (error) => {
      toast.error(
        getApiErrorMessage(error, "Failed to process workplace request updates")
      );
      logError("Update multiple workplace requests error:", error);
    }
  });

  const updateMultipleWorkplaceRequests = useCallback(
    (requests: WorkplaceRequestUpdateEntry[]) => {
      return updateMultipleWorkplaceRequestsMutation.mutateAsync(requests);
    },
    [updateMultipleWorkplaceRequestsMutation]
  );

  const createWorkplaceRequestMutation = useMutation({
    mutationFn: async (request: CreateWorkplaceRequestParams) => {
      const requestWithTz = {
        ...request,
        tz: tz ?? deviceTz
      };
      const res =
        await api.client.workplaceRequests.createWorkplaceRequest(
          requestWithTz
        );
      return res.result;
    },

    onSuccess: () => {
      updateAll();
      toast.success("Workplace request created successfully");
    },

    onError: (error) => {
      toast.error(
        getApiErrorMessage(error, "Failed to create workplace request")
      );
      logError("Create workplace request error:", error);
    }
  });

  const createWorkplaceRequest = useCallback(
    (request: CreateWorkplaceRequestParams) => {
      return createWorkplaceRequestMutation.mutateAsync(request);
    },
    [createWorkplaceRequestMutation]
  );

  const createMultipleWorkplaceRequestsMutation = useMutation({
    mutationFn: async (requests: CreateWorkplaceRequestParams[]) => {
      const requestsWithTz = requests.map((request) => ({
        ...request,
        tz: tz ?? deviceTz
      }));

      // Create all requests in parallel with individual error handling
      const results = await Promise.allSettled(
        requestsWithTz.map((request, index) =>
          api.client.workplaceRequests
            .createWorkplaceRequest(request)
            .then((res) => ({
              success: true,
              requestId: `index-${index}`,
              result: res.result
            }))
            .catch((error) => ({
              success: false,
              requestId: `index-${index}`,
              error
            }))
        )
      );

      return processBatchResults(results);
    },

    onSuccess: (results) => {
      updateAll();
      const { totalSuccessful, totalFailed, totalRequested } = results;
      showBatchToast(
        { verb: "create", past: "created" },
        totalSuccessful,
        totalFailed,
        totalRequested
      );
    },

    onError: (error) => {
      toast.error(
        getApiErrorMessage(error, "Failed to process workplace requests")
      );
      logError("Create multiple workplace requests error:", error);
    }
  });

  const createMultipleWorkplaceRequests = useCallback(
    (requests: CreateWorkplaceRequestParams[]) => {
      return createMultipleWorkplaceRequestsMutation.mutateAsync(requests);
    },
    [createMultipleWorkplaceRequestsMutation]
  );

  // Memoized loading state
  const isLoading = useMemo(
    () =>
      updateWorkplaceRequestStatusMutation.isPending ||
      updateWorkplaceRequestOwnerMutation.isPending ||
      deleteWorkplaceRequestMutation.isPending ||
      updateWorkplaceRequestMutation.isPending ||
      respondToApprovalMutation.isPending ||
      updateMultipleWorkplaceRequestsMutation.isPending ||
      createWorkplaceRequestMutation.isPending ||
      createMultipleWorkplaceRequestsMutation.isPending,
    [
      updateWorkplaceRequestStatusMutation.isPending,
      updateWorkplaceRequestOwnerMutation.isPending,
      deleteWorkplaceRequestMutation.isPending,
      updateWorkplaceRequestMutation.isPending,
      respondToApprovalMutation.isPending,
      updateMultipleWorkplaceRequestsMutation.isPending,
      createWorkplaceRequestMutation.isPending,
      createMultipleWorkplaceRequestsMutation.isPending
    ]
  );

  return {
    updateWorkplaceRequestStatus,
    updateWorkplaceRequestStatusMutation,
    updateWorkplaceRequestOwner,
    updateWorkplaceRequestOwnerMutation,
    deleteWorkplaceRequest,
    deleteWorkplaceRequestMutation,
    updateWorkplaceRequest,
    updateWorkplaceRequestMutation: {
      ...updateWorkplaceRequestMutation,
      isLoading: updateWorkplaceRequestMutation.isPending
    },
    respondToApproval,
    respondToApprovalMutation,
    updateMultipleWorkplaceRequests,
    updateMultipleWorkplaceRequestsMutation,
    createWorkplaceRequest,
    createWorkplaceRequestMutation,
    createMultipleWorkplaceRequests,
    createMultipleWorkplaceRequestsMutation,
    isLoading
  };
}
